import { Button, EmptyState, formatTimeAgo, Skeleton } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useNavigate } from '@tanstack/react-router';
import { Ban, BellOff, ClipboardList, PackageMinus, PackageX, Truck } from 'lucide-react';
import type { ReactNode } from 'react';
import { usePageContext } from '../usePageContext.ts';
import { notificationText, targetOf, type Notification, type NotificationKind } from './notifications.ts';
import styles from './Notifications.module.css';
import { useMarkRead, type NotificationFilter } from './useNotifications.ts';
import { ORDERS_DEFAULTS } from '../../app/search-params.ts';

const SKELETON_ROWS = 4;

const ICON_OF_KIND: Readonly<Record<NotificationKind, ReactNode>> = {
  stock_low: <PackageMinus size={18} aria-hidden="true" />,
  stock_out: <PackageX size={18} aria-hidden="true" />,
  dispatch_failed: <Truck size={18} aria-hidden="true" />,
  carrier_canceled: <Ban size={18} aria-hidden="true" />,
  daily_digest: <ClipboardList size={18} aria-hidden="true" />,
};

export const useChooseNotification = (api: TypedClient, afterChoice: () => void) => {
  const navigate = useNavigate();
  const markRead = useMarkRead(api);
  return (notification: Notification) => {
    if (!notification.read) markRead.mutate({ kind: 'one', notificationId: notification.id });
    afterChoice();
    const target = targetOf(notification);
    if (target.kind === 'order')
      void navigate({ to: '/orders', search: { ...ORDERS_DEFAULTS, order: target.orderId } });
    if (target.kind === 'product')
      void navigate({ to: '/products/$productId', params: { productId: target.productId } });
    if (target.kind === 'view')
      void navigate({ to: '/orders', search: { ...ORDERS_DEFAULTS, view: target.view } });
  };
};

const NotificationItem = ({
  notification,
  now,
  onChoose,
}: {
  notification: Notification;
  now: Date;
  onChoose: (notification: Notification) => void;
}) => {
  const { t, locale } = usePageContext();
  const text = notificationText(notification, t, locale);
  return (
    <li>
      <button
        type="button"
        className={styles['item']}
        data-kind={notification.kind}
        data-unread={notification.read ? undefined : 'true'}
        onClick={() => {
          onChoose(notification);
        }}
      >
        <span className={styles['icon']}>{ICON_OF_KIND[notification.kind]}</span>
        <span className={styles['text']}>
          <span className={styles['title']}>{text.title}</span>
          <span className={styles['detail']}>{text.detail}</span>
          <time className={styles['time']} dateTime={notification.createdAt}>
            {formatTimeAgo(notification.createdAt, locale, now)}
          </time>
        </span>
        {notification.read ? null : (
          <span className={styles['dot']}>
            <span className="visually-hidden">{t('notifications.unread')}</span>
          </span>
        )}
      </button>
    </li>
  );
};

const Section = ({
  title,
  items,
  onChoose,
}: {
  title: string;
  items: readonly Notification[];
  onChoose: (notification: Notification) => void;
}) => {
  const now = new Date();
  return items.length === 0 ? null : (
    <section className={styles['section']} aria-label={title}>
      <h3 className={styles['sectionTitle']}>{title}</h3>
      <ul className={styles['list']}>
        {items.map((notification) => (
          <NotificationItem key={notification.id} notification={notification} now={now} onChoose={onChoose} />
        ))}
      </ul>
    </section>
  );
};

export type NotificationListProps = {
  filter: NotificationFilter;
  loading: boolean;
  error: string | undefined;
  onRetry: () => void;
  items: readonly Notification[];
  onChoose: (notification: Notification) => void;
};

export const NotificationList = ({
  filter,
  loading,
  error,
  onRetry,
  items,
  onChoose,
}: NotificationListProps) => {
  const { t } = usePageContext();
  if (loading)
    return (
      <div className={styles['skeletons']}>
        {Array.from({ length: SKELETON_ROWS }, (_, index) => (
          <Skeleton key={index} height="52px" />
        ))}
      </div>
    );
  if (error !== undefined)
    return (
      <div role="alert" className={styles['error']}>
        <p>{error}</p>
        <Button variant="outline" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </div>
    );
  if (items.length === 0)
    return filter === 'unread' ? (
      <EmptyState
        icon={<BellOff size={24} />}
        title={t('notifications.allRead')}
        description={t('notifications.allReadHelp')}
      />
    ) : (
      <EmptyState
        icon={<BellOff size={24} />}
        title={t('notifications.empty')}
        description={t('notifications.emptyHelp')}
      />
    );
  return (
    <>
      <Section
        title={t('notifications.sectionNew')}
        items={items.filter((notification) => !notification.read)}
        onChoose={onChoose}
      />
      <Section
        title={t('notifications.sectionEarlier')}
        items={items.filter((notification) => notification.read)}
        onChoose={onChoose}
      />
    </>
  );
};
